<template>
  <div class="step-question-template w-full">
    <div class="w-full">
      <div role="heading" aria-level="3" class="question-text" v-html="question.questionText"></div>
      <div class="question-description" v-if="question.questionDescription" v-html="question.questionDescription"></div>
    </div>
    <div class="w-full flex flex-row items-stretch gap-2 my-10">
      <div class="w-1/3" v-if="!isUs">
        <!-- non-US: Day comes first. -->
        <select
          id="dayOfBirth"
          name="day-of-birth"
          title="day of birth"
          aria-label="day of birth"
          v-model="dayOfBirth"
          class="inline-block w-full rounded-md p-2.5 border border-solid border-zinc-300"
        >
          <option value disabled selected>Day</option>
          <option v-for="day in 31" :value="String(day).padStart(2, '0')">
            {{ day }}
          </option>
        </select>
      </div>
      <div class="w-1/3">
        <select
          id="monthOfBirth"
          name="month-of-birth"
          title="month of birth"
          aria-label="month of birth"
          v-model="monthOfBirth"
          class="inline-block w-full rounded-md p-2.5 border border-solid border-zinc-300"
        >
          <option value disabled selected>Month</option>
          <option value="01">January</option>
          <option value="02">February</option>
          <option value="03">March</option>
          <option value="04">April</option>
          <option value="05">May</option>
          <option value="06">June</option>
          <option value="07">July</option>
          <option value="08">August</option>
          <option value="09">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>
      </div>
      <div class="w-1/3" v-if="isUs">
        <!-- US only: Month comes first. -->
        <select
          id="dayOfBirth"
          name="day-of-birth"
          title="day of birth"
          aria-label="day of birth"
          v-model="dayOfBirth"
          class="inline-block w-full rounded-md p-2.5 border border-solid border-zinc-300"
        >
          <option value disabled selected>Day</option>
          <option v-for="day in 31" :value="String(day).padStart(2, '0')">
            {{ day }}
          </option>
        </select>
      </div>
      <div class="w-1/3">
        <select
          id="yearOfBirth"
          name="year-of-birth"
          title="year of birth"
          aria-label="year of birth"
          v-model="yearOfBirth"
          class="inline-block w-full rounded-md p-2.5 border border-solid border-zinc-300"
        >
          <option value disabled selected>Year</option>
          <option v-for="year in yearsSince1905" :value="year">
            {{ year }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import poster from "../../mixins/poster";
import validator from "../../mixins/validator";
import { DateOfBirthQuestion } from "@/Consultation/types";

export default Vue.extend({
  mixins: [poster, validator],
  inject: ["isUs", "submitUrl", "csrfHeader", "csrf"],
  props: {
    question: {
      required: true,
      type: Object as PropType<DateOfBirthQuestion>,
    },
    showRequired: { required: true },
  },
  data() {
    return {
      dayOfBirth: "",
      monthOfBirth: "",
      yearOfBirth: "",
      updatedQuestion: this.question,
    };
  },
  created() {
    this.$emit("answer", this.updatedQuestion, this.getResponse());
    this.$emit("update-next-button-status", this.isComplete());

    if (!!this.updatedQuestion.response) {
      const date = new Date(this.updatedQuestion.response);
      this.dayOfBirth = date.getDate().toString().padStart(2, "0");
      this.monthOfBirth = (date.getMonth() + 1).toString().padStart(2, "0");
      this.yearOfBirth = date.getFullYear().toString();
    }
  },
  methods: {
    isComplete() {
      return !!this.dayOfBirth && !!this.monthOfBirth && !!this.yearOfBirth;
    },
    dateString() {
      if (!this.isComplete()) {
        return undefined;
      }

      return this.yearOfBirth + "-" + this.monthOfBirth + "-" + this.dayOfBirth;
    },
    isAcceptableAnswer() {
      if (!this.isComplete()) {
        return false;
      }

      // https://stackoverflow.com/a/15555947

      const dateOfBirth = +new Date(this.dateString());
      const patientAge = ~~((Date.now() - dateOfBirth) / 31557600000);

      if (this.question.minAge && patientAge < this.question.minAge) {
        return false; // Patient is too young.
      }

      if (this.question.maxAge && patientAge > this.question.maxAge) {
        return false; // Patient is too old.
      }

      return true;
    },
    getResponse() {
      if (!this.isComplete()) {
        return null;
      }

      return {
        response: this.updatedQuestion.response,
        isChangedAnswer: false,
      };
    },
    answerUpdated() {
      if (!this.isComplete()) {
        return;
      }

      this.updatedQuestion.response = this.dateString();
      this.updatedQuestion.isChangedAnswer = false;

      this.post(this.getResponse());
      this.$emit("update-next-button-status", this.isComplete());

      // Handle immediate rejection.
      if (this.isAcceptableAnswer === false && this.updatedQuestion.redirectFailure != null) {
        this.updatedQuestion.redirectOnSubmit = this.updatedQuestion.redirectFailure;
      } else {
        this.updatedQuestion.redirectOnSubmit = null;
      }
    },
  },
  computed: {
    valid() {
      return typeof this.errorMessage !== "string";
    },
    yearsSince1905() {
      const year = new Date().getFullYear() - 18;
      return Array.from({ length: year - 1905 }, (value, index) => 1906 + index).reverse();
    },
  },
  watch: {
    dayOfBirth() {
      this.answerUpdated();
    },
    monthOfBirth() {
      this.answerUpdated();
    },
    yearOfBirth() {
      this.answerUpdated();
    },
  },
});
</script>
